import { FormApi } from "final-form";
import { noop } from "lodash";
import { useTranslation } from "next-i18next";
import { FC, SyntheticEvent } from "react";
import { Form } from "react-final-form";
import FormTextField from "@Components/forms/formFields/textField/FormTextField";
import { FormMessage } from "@Components/forms/formMessage/FormMessage";
import CTFieldValidator, {
  email,
  required,
} from "@Components/forms/helpers/FieldValidator";
import { submitFormActionForgotPassword } from "@Components/forms/helpers/commonFormSubmission";
import { formStateChangeHandler } from "@Components/forms/helpers/handlers";
import { Button } from "@Components/ui";
import { AUTO_ERROR_CODE_MESSAGE_TRANSLATION } from "@Constants/snackbar";
import { IloginFormSection } from "@Types/login-page";
import { useForgotPassword } from "@wff/hooks/useAuthHooks";
import {
  FORM_TYPES,
  useFormFieldValidation,
} from "@wff/hooks/useFormFieldValidation";
import { useAppDispatch, useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";

interface Props {
  onClickCancel: () => void;
  isCheckout?: boolean;
  loginFormSection?: IloginFormSection;
}

const ForgotPasswordForm: FC<Props> = ({
  onClickCancel,
  isCheckout,
  loginFormSection,
}) => {
  const forgotPassInputPlaceholderEmail =
    loginFormSection?.forgotPassInputPlaceholderEmail;
  const forgotPassSubmitButtonText =
    loginFormSection?.forgotPassSubmitButtonText;
  const forgotPassCancelButtonText =
    loginFormSection?.forgotPassCancelButtonText;
  const forgotPassErrorRequiredEmail =
    loginFormSection?.forgotPassErrorRequiredEmail;
  const forgotPassErrorInvalidEmail =
    loginFormSection?.forgotPassErrorInvalidEmail;
  const EmailInputLabel = forgotPassInputPlaceholderEmail
    ? forgotPassInputPlaceholderEmail
    : "";

  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const currentLocale = useAppSelector(localeSelector);
  const {
    forgotPasswordState,
    setForgotPasswordState,
    forgotPasswordStateHandler,
  } = useForgotPassword();

  const errorCodeTranslation = {
    WF_CUSTOMER_001: t("customer.form_errors.existing_account_html"),
    WF_CUSTOMER_002: t("customer.form_errors.blank_email"),
    WF_CUSTOMER_008: AUTO_ERROR_CODE_MESSAGE_TRANSLATION.WF_CUSTOMER_008(
      currentLocale?.languageCode
    ),
  };
  const validationTranslation = {
    allFieldsBlank: t("customer.form_errors.all_fields_blank"),
    emailBlank: t("customer.form_errors.blank_email"),
    emailNeedsContentAfterAtSymbol: t(
      "customer.form_errors.format_ends_with_at"
    ),
  };

  const {
    touched,
    setTouched,
    setFormError,
    formErrorMessages,
    setFormActionIsActive,
  } = useFormFieldValidation({
    type: FORM_TYPES.FORGOT_PASSWORD,
    touched: true, // this form does not require touching for validation
    formState: forgotPasswordState,
    languageCode: currentLocale?.languageCode,
    errorCodeTranslation,
    validationTranslation,
    formAction: () => {
      submitFormActionForgotPassword({
        dispatch,
        payload: forgotPasswordState,
        setFormError,
        errorCodeTranslation,
        languageCode: currentLocale?.languageCode,
      });
    },
  });

  const handleOnClickSubmit = (
    e: SyntheticEvent<HTMLButtonElement, MouseEvent>,
    form: FormApi<any, Partial<any>>
  ) => {
    e.preventDefault();
    setFormActionIsActive(true);
    setTimeout(() => {
      form.reset();
      forgotPasswordStateHandler("email", "");
    }, 100);
  };

  return (
    <Form
      data-testid="forgot-password-form"
      onSubmit={noop}
      render={({ form }) => (
        <form className="form form--recover-customer-password" onSubmit={noop}>
          <FormMessage
            message={t("form.error_heading")}
            type="error"
            errors={formErrorMessages}
          />

          <FormTextField
            name="email"
            label={EmailInputLabel}
            data-testid="form_field-email"
            validate={CTFieldValidator(
              [
                required(forgotPassErrorRequiredEmail),
                email(forgotPassErrorInvalidEmail),
              ],
              "Email"
            )}
            outlined={!isCheckout}
            value={forgotPasswordState?.email}
            onChange={(e) => {
              formStateChangeHandler({
                key: "email",
                value: e.target.value,
                formState: forgotPasswordState,
                setTouched,
                formStateSetter: setForgotPasswordState,
              });
            }}
          />
          <div className="form__actions">
            <Button
              className="action"
              data-testid="form_field-button-submit"
              onClick={(e) => handleOnClickSubmit(e, form)}
              disabled={!touched}
              type="submit"
            >
              {forgotPassSubmitButtonText}
            </Button>
            <Button className="action action--feature" onClick={onClickCancel}>
              {forgotPassCancelButtonText}
            </Button>
          </div>
        </form>
      )}
    />
  );
};

export default ForgotPasswordForm;
